import React, { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { rolesAPI } from "../api/roles";
import { getAllTemplates } from "../api/templates";
import {
  TrashIcon,
  PencilSquareIcon,
  XMarkIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import PermissionGuard from "../components/common/PermissionGuard";
import PagePermissionGuard from "../components/common/PagePermissionGuard";
import { useAuth } from "../contexts/AuthContext";
import { usePageHeader } from "../contexts/PageHeaderContext";
import BreadCrumb from "../components/common/BreadCrumb";
import {
  AnimatedModal,
  DataGrid,
  Input,
  Dropdown,
  Checkbox,
  TableSkeleton,
} from "../components/ui";

const Roles = () => {
  const { organization } = useAuth();
  const { setHeaderContent } = usePageHeader();
  const hasRoleControl = organization?.planMeta?.ROLE_CONTROL === true;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [roles, setRoles] = useState([]);
  const [editingRole, setEditingRole] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [availableDocumentTypes, setAvailableDocumentTypes] = useState([]);
  const [newDocumentType, setNewDocumentType] = useState("");
  const validationSchema = Yup.object({
    name: Yup.string().required("Role name is required"),
    description: Yup.string().required("Description is required"),
    permissions: Yup.object({
      dashboard: Yup.object({
        enable: Yup.boolean(),
      }),
      templates: Yup.object({
        enable: Yup.boolean(),
        read: Yup.boolean(),
        write: Yup.boolean(),
        update: Yup.boolean(),
        delete: Yup.boolean(),
        use_template: Yup.boolean(),
        documentTypes: Yup.array().of(Yup.string()),
      }),
      documents: Yup.object({
        enable: Yup.boolean(),
        read: Yup.boolean(),
        delete: Yup.boolean(),
      }),
      members: Yup.object({
        enable: Yup.boolean(),
        read: Yup.boolean(),
        write: Yup.boolean(),
        update: Yup.boolean(),
      }),
      bulk_send: Yup.object({
        enable: Yup.boolean(),
        read: Yup.boolean(),
        write: Yup.boolean(),
      }),
      subscription_usage: Yup.object({
        enable: Yup.boolean(),
        read: Yup.boolean(),
      }),
      roles: Yup.object({
        enable: Yup.boolean(),
        read: Yup.boolean(),
        write: Yup.boolean(),
        update: Yup.boolean(),
        delete: Yup.boolean(),
      }),
    }),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      permissions: {
        dashboard: { enable: true },
        templates: {
          enable: true,
          read: true,
          write: false,
          update: false,
          delete: false,
          use_template: true,
          documentTypes: [],
        },
        documents: {
          enable: true,
          read: true,
          delete: false,
        },
        members: {
          enable: false,
          read: false,
          write: false,
          update: false,
        },
        bulk_send: {
          enable: false,
          read: false,
          write: false,
        },
        subscription_usage: {
          enable: false,
          read: false,
        },
        roles: {
          enable: false,
          read: false,
          write: false,
          update: false,
          delete: false,
        },
      },
    },
    validationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        if (editingRole) {
          await rolesAPI.updateRole(editingRole._id, values);
          toast.success("Role updated successfully");
        } else {
          await rolesAPI.createRole(values);
          toast.success("Role created successfully");
        }
        fetchRoles();
        resetForm();
        closeSidebar();
        setEditingRole(null);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to save role");
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Handle sidebar animation
  useEffect(() => {
    if (isSidebarOpen) {
      // Wait for next tick to trigger transition
      setTimeout(() => setSidebarVisible(true), 10);
    } else {
      setSidebarVisible(false);
    }
  }, [isSidebarOpen]);

  const closeSidebar = () => {
    setSidebarVisible(false);
    setTimeout(() => {
      setIsSidebarOpen(false);
      setEditingRole(null);
      formik.resetForm();
    }, 300);
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    const permissions = role.permissions || formik.initialValues.permissions;
    // Ensure documentTypes array exists
    if (permissions.templates && !permissions.templates.documentTypes) {
      permissions.templates.documentTypes = [];
    }
    formik.setValues({
      name: role.name || "",
      description: role.description || "",
      permissions: permissions,
    });
    setIsSidebarOpen(true);
  };

  const handleDelete = async (roleId) => {
    setRoleToDelete(roleId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!roleToDelete) return;
    try {
      await rolesAPI.deleteRole(roleToDelete);
      toast.success("Role deleted successfully");
      fetchRoles();
      setShowDeleteModal(false);
      setRoleToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete role");
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setRoleToDelete(null);
  };

  const fetchRoles = async () => {
    try {
      setIsLoading(true);
      const response = await rolesAPI.getRoles();
      setRoles(response?.data || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error(error.response?.data?.message || "Failed to fetch roles");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchDocumentTypes();
  }, []);

  const openAddRoleSidebar = () => {
    setEditingRole(null);
    formik.resetForm();
    setIsSidebarOpen(true);
  };

  const openAddRoleSidebarRef = useRef(openAddRoleSidebar);
  openAddRoleSidebarRef.current = openAddRoleSidebar;

  useEffect(() => {
    setHeaderContent(
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full">
        <div>
          <BreadCrumb
            items={[
              { label: "Dashboard", href: "/dashboard" },
              { label: "Roles" },
            ]}
            className="mb-2"
          />
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-gray-900">
            Roles Management
          </h1>
          <p className="text-sm text-gray-600 max-w-2xl mt-1">
            Manage your roles and view details
          </p>
        </div>
        <PermissionGuard module="roles" action="write">
          {hasRoleControl && (
            <button
              onClick={() => openAddRoleSidebarRef.current()}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors duration-200 text-sm font-medium shrink-0"
            >
              <UserPlusIcon className="h-5 w-5" />
              Add Role
            </button>
          )}
        </PermissionGuard>
      </div>,
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent, hasRoleControl]);

  const fetchDocumentTypes = async () => {
    try {
      const response = await getAllTemplates("");
      const templates = response.data || [];
      // Extract unique document types
      const uniqueTypes = [
        ...new Set(
          templates
            .map((t) => t.documentType)
            .filter((type) => type && type.trim() !== ""),
        ),
      ].sort();
      // Add "others" as a default option for backward compatibility
      const typesWithOthers = uniqueTypes.includes("others")
        ? uniqueTypes
        : ["others", ...uniqueTypes];
      setAvailableDocumentTypes(typesWithOthers);
    } catch (error) {
      console.error("Error fetching document types:", error);
      // Set "others" as default even on error
      setAvailableDocumentTypes(["others"]);
    }
  };

  const updatePermission = (module, key, value) => {
    formik.setFieldValue(`permissions.${module}.${key}`, value);
  };

  const addDocumentType = (type) => {
    if (!type || type.trim() === "") return;
    const currentTypes =
      formik.values.permissions.templates.documentTypes || [];
    const trimmedType = type.trim();
    if (!currentTypes.includes(trimmedType)) {
      const newTypes = [...currentTypes, trimmedType];
      formik.setFieldValue("permissions.templates.documentTypes", newTypes);
      setNewDocumentType("");
    }
  };

  const removeDocumentType = (typeToRemove) => {
    const currentTypes =
      formik.values.permissions.templates.documentTypes || [];
    const newTypes = currentTypes.filter((type) => type !== typeToRemove);
    formik.setFieldValue("permissions.templates.documentTypes", newTypes);
  };

  const getPermissionCount = (permissions) => {
    if (!permissions) return 0;
    return Object.keys(permissions).filter(
      (module) => permissions[module]?.enable,
    ).length;
  };

  return (
    <PagePermissionGuard
      module="roles"
      action="read"
      pageName="Roles Management"
    >
      <div>
        <div className="bg-white overflow-hidden">
          {isLoading ? (
            <div className="overflow-hidden">
              <TableSkeleton rows={8} cols={5} showHeader={true} />
            </div>
          ) : roles.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <PencilSquareIcon className="h-6 w-6 text-gray-400" />
                </div>
                <p className="text-lg font-medium text-gray-900">
                  No roles found
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Get started by creating your first role
                </p>
                <PermissionGuard module="roles" action="write">
                  {hasRoleControl && (
                    <button
                      onClick={openAddRoleSidebar}
                      className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:opacity-90 transition-colors text-sm font-medium"
                    >
                      <UserPlusIcon className="h-5 w-5" />
                      Create Role
                    </button>
                  )}
                </PermissionGuard>
              </div>
            </div>
          ) : (
            <DataGrid
              columns={[
                {
                  key: "name",
                  label: "Role Name",
                  sortable: false,
                  render: (_, row) => (
                    <span className="text-sm font-medium text-gray-900">
                      {row.name}
                    </span>
                  ),
                },
                {
                  key: "description",
                  label: "Description",
                  sortable: false,
                  render: (_, row) => (
                    <span className="text-sm text-gray-500 max-w-xs truncate block">
                      {row.description}
                    </span>
                  ),
                },
                {
                  key: "permissions",
                  label: "Permissions",
                  sortable: false,
                  render: (_, row) => (
                    <span className="text-sm text-gray-500">
                      {getPermissionCount(row.permissions)} modules
                    </span>
                  ),
                },
                {
                  key: "userCount",
                  label: "Associated Members",
                  sortable: false,
                  render: (_, row) => (
                    <span className="text-sm text-gray-500">
                      {row.userCount}
                    </span>
                  ),
                },
                {
                  key: "actions",
                  label: "Actions",
                  sortable: false,
                  render: (_, row) => (
                    <div
                      className="flex items-center gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <PermissionGuard module="roles" action="update">
                        {hasRoleControl && (
                          <button
                            onClick={() => handleEdit(row)}
                            className="text-gray-600 hover:text-primary transition-colors duration-200"
                            title="Edit role"
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </button>
                        )}
                      </PermissionGuard>
                      <PermissionGuard module="roles" action="delete">
                        <button
                          onClick={() => handleDelete(row._id)}
                          className="text-red-600 hover:text-red-900 transition-colors duration-200"
                          title="Delete role"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </PermissionGuard>
                    </div>
                  ),
                },
              ]}
              data={roles.map((r) => ({ ...r, id: r._id || r.id }))}
              headerVariant="white"
              emptyMessage="No roles found. Get started by creating your first role."
              className="!rounded-none !shadow-none"
              minHeight={320}
            />
          )}

          {/* Add/Edit Role Sidebar */}
          {isSidebarOpen && (
            <div className="fixed inset-0 z-50">
              {/* Overlay */}
              <div
                className={`fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
                  sidebarVisible ? "opacity-100" : "opacity-0"
                }`}
                onClick={closeSidebar}
              />
              {/* Sidebar Panel */}
              <div className="fixed inset-y-0 right-0 flex max-w-full">
                <div
                  className={`w-screen max-w-2xl transform transition-transform duration-300 ease-in-out bg-white shadow-xl h-full flex flex-col ${
                    sidebarVisible ? "translate-x-0" : "translate-x-full"
                  }`}
                >
                  <div className="px-6 py-3 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h2 className="font-heading text-lg font-semibold text-gray-900">
                        {editingRole ? "Edit Role" : "Add New Role"}
                      </h2>
                      <button
                        type="button"
                        onClick={closeSidebar}
                        className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
                      >
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto sidebar-scroll">
                    <div className="px-6 py-6">
                      <form
                        onSubmit={formik.handleSubmit}
                        className="space-y-6"
                      >
                        {/* Basic Information */}
                        <div className="space-y-4">
                          <div>
                            <Input
                              id="name"
                              name="name"
                              label="Role Name"
                              type="text"
                              value={formik.values.name}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              placeholder="Enter role name"
                              error={
                                formik.errors.name && formik.touched.name
                                  ? formik.errors.name
                                  : undefined
                              }
                              size="sm"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="description"
                              className="block text-sm font-semibold text-gray-700 mb-2"
                            >
                              Description
                            </label>
                            <textarea
                              id="description"
                              name="description"
                              value={formik.values.description}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              rows={3}
                              className={`block w-full px-4 py-2.5 border rounded-xl bg-white placeholder-gray-500 font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm ${
                                formik.errors.description &&
                                formik.touched.description
                                  ? "border-red-500 focus:ring-red-500/20"
                                  : "border-gray-300 hover:border-gray-400"
                              }`}
                              placeholder="Enter role description"
                            />
                            {formik.errors.description &&
                              formik.touched.description && (
                                <p className="mt-1.5 text-sm text-red-600">
                                  {formik.errors.description}
                                </p>
                              )}
                          </div>
                        </div>

                        {/* Permissions */}
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium text-gray-900">
                              Permissions
                            </h3>
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={() => {
                                  // Enable all modules
                                  Object.keys(
                                    formik.values.permissions,
                                  ).forEach((module) => {
                                    updatePermission(module, "enable", true);
                                  });
                                }}
                                className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                              >
                                Enable All
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  // Disable all modules
                                  Object.keys(
                                    formik.values.permissions,
                                  ).forEach((module) => {
                                    updatePermission(module, "enable", false);
                                  });
                                }}
                                className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                              >
                                Disable All
                              </button>
                            </div>
                          </div>

                          <div className="space-y-4">
                            {Object.entries(formik.values.permissions).map(
                              ([module, permissions]) => (
                                <div
                                  key={module}
                                  className={`border rounded-lg transition-all duration-200 ${
                                    permissions.enable
                                      ? "border-primary/20 bg-primary/5 shadow-sm"
                                      : "border-gray-200 bg-gray-50"
                                  }`}
                                >
                                  <div className="p-4">
                                    <div className="flex items-center justify-between mb-3">
                                      <div className="flex items-center space-x-3">
                                        <div
                                          className={`w-3 h-3 rounded-full ${
                                            permissions.enable
                                              ? "bg-primary"
                                              : "bg-gray-300"
                                          }`}
                                        ></div>
                                        <h4 className="text-sm font-semibold text-gray-900 capitalize">
                                          {module.replace("_", " ")}
                                        </h4>
                                      </div>
                                      <Checkbox
                                        checked={permissions.enable}
                                        onChange={(e) =>
                                          updatePermission(
                                            module,
                                            "enable",
                                            e.target.checked,
                                          )
                                        }
                                        label={
                                          permissions.enable
                                            ? "Enabled"
                                            : "Disabled"
                                        }
                                      />
                                    </div>

                                    {permissions.enable && (
                                      <div className="ml-6">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                          {Object.entries(permissions).map(
                                            ([key, value]) => {
                                              if (
                                                key === "enable" ||
                                                key === "documentTypes"
                                              )
                                                return null;
                                              return (
                                                <div
                                                  key={key}
                                                  className="flex items-center p-2 rounded-md hover:bg-white/50 transition-colors"
                                                >
                                                  <Checkbox
                                                    checked={value}
                                                    onChange={(e) =>
                                                      updatePermission(
                                                        module,
                                                        key,
                                                        e.target.checked,
                                                      )
                                                    }
                                                    label={
                                                      <span className="capitalize">
                                                        {key.replace("_", " ")}
                                                        {value && (
                                                          <span className="ml-2 inline-block w-2 h-2 bg-primary rounded-full align-middle" />
                                                        )}
                                                      </span>
                                                    }
                                                  />
                                                </div>
                                              );
                                            },
                                          )}
                                        </div>

                                        {/* Document Types Selection for Templates */}
                                        {module === "templates" && (
                                          <div className="mt-4 pt-4 border-t border-gray-200">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                              Allowed Document Types
                                            </label>
                                            <p className="text-xs text-gray-500 mb-3">
                                              Select which document types this
                                              role can access. If none are
                                              selected, all document types will
                                              be accessible.
                                            </p>

                                            {/* Selected Document Types */}
                                            {formik.values.permissions.templates
                                              .documentTypes &&
                                              formik.values.permissions
                                                .templates.documentTypes
                                                .length > 0 && (
                                                <div className="flex flex-wrap gap-2 mb-3">
                                                  {formik.values.permissions.templates.documentTypes.map(
                                                    (docType) => (
                                                      <span
                                                        key={docType}
                                                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20"
                                                      >
                                                        {docType}
                                                        <button
                                                          type="button"
                                                          onClick={() =>
                                                            removeDocumentType(
                                                              docType,
                                                            )
                                                          }
                                                          className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-primary/20 transition-colors"
                                                        >
                                                          <XMarkIcon className="h-3 w-3" />
                                                        </button>
                                                      </span>
                                                    ),
                                                  )}
                                                </div>
                                              )}

                                            {/* Add Document Type */}
                                            <div>
                                              <Dropdown
                                                value={newDocumentType}
                                                onChange={(val) => {
                                                  if (val) {
                                                    addDocumentType(val);
                                                    setNewDocumentType("");
                                                  }
                                                }}
                                                placeholder="Select a document type"
                                                options={availableDocumentTypes
                                                  .filter(
                                                    (type) =>
                                                      !formik.values.permissions.templates.documentTypes?.includes(
                                                        type,
                                                      ),
                                                  )
                                                  .map((type) => ({
                                                    value: type,
                                                    label:
                                                      type === "others"
                                                        ? `${type} (for templates without document type)`
                                                        : type,
                                                  }))}
                                                size="sm"
                                              />
                                            </div>
                                          </div>
                                        )}
                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                          <div className="flex items-center justify-between text-xs text-gray-500">
                                            <span>
                                              {
                                                Object.entries(
                                                  permissions,
                                                ).filter(
                                                  ([key, value]) =>
                                                    key !== "enable" && value,
                                                ).length
                                              }{" "}
                                              of{" "}
                                              {Object.keys(permissions).length -
                                                1}{" "}
                                              permissions enabled
                                            </span>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                // Enable all permissions for this module
                                                Object.entries(
                                                  permissions,
                                                ).forEach(([key, value]) => {
                                                  if (key !== "enable") {
                                                    updatePermission(
                                                      module,
                                                      key,
                                                      true,
                                                    );
                                                  }
                                                });
                                              }}
                                              className="text-primary hover:text-primary-dark font-medium"
                                            >
                                              Enable All
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ),
                            )}
                          </div>

                          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-start space-x-2">
                              <div className="w-5 h-5 text-blue-500 mt-0.5">
                                <svg fill="currentColor" viewBox="0 0 20 20">
                                  <path
                                    fillRule="evenodd"
                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                              <div className="text-sm text-blue-700">
                                <p className="font-medium">
                                  Permission Guidelines
                                </p>
                                <p className="text-blue-600 mt-1">
                                  Enable modules to grant access to specific
                                  features. Each module has granular permissions
                                  that can be individually controlled.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Form Actions */}
                        <PermissionGuard
                          module="roles"
                          action={editingRole ? "update" : "write"}
                        >
                          {hasRoleControl && (
                            <div className="pt-4 border-t border-gray-200">
                              <button
                                type="submit"
                                disabled={formik.isSubmitting}
                                className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-primary hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                              >
                                {formik.isSubmitting ? (
                                  <>
                                    <svg
                                      className="animate-spin h-5 w-5"
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      />
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      />
                                    </svg>
                                    {editingRole
                                      ? "Updating..."
                                      : "Creating..."}
                                  </>
                                ) : editingRole ? (
                                  <>
                                    <PencilSquareIcon className="h-5 w-5" />
                                    Update Role
                                  </>
                                ) : (
                                  <>
                                    <UserPlusIcon className="h-5 w-5" />
                                    Create Role
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </PermissionGuard>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          <AnimatedModal
            isOpen={showDeleteModal}
            onClose={cancelDelete}
            closeOnBackdrop
            contentClassName="bg-white shadow-xl overflow-hidden sm:max-w-lg sm:w-full"
          >
            <div className="bg-white px-6 py-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <TrashIcon className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Delete Role
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Are you sure you want to delete this role? This action
                    cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={cancelDelete}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete
              </button>
            </div>
          </AnimatedModal>
        </div>
      </div>
    </PagePermissionGuard>
  );
};

export default Roles;
